import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plug, Key, Eye, EyeSlash, Check, X, Info, Link
} from '@phosphor-icons/react';

const INTEGRATION_CONFIGS = [
  { id: 'resend_api_key', label: 'Resend API Key', category: 'Email', description: 'Email delivery service', docsUrl: 'https://resend.com/docs' },
  { id: 'openai_api_key', label: 'OpenAI API Key', category: 'AI', description: 'GPT models (if not using Emergent LLM Key)', docsUrl: 'https://platform.openai.com/api-keys' },
  { id: 'anthropic_api_key', label: 'Anthropic API Key', category: 'AI', description: 'Claude models (if not using Emergent LLM Key)', docsUrl: 'https://console.anthropic.com' },
  { id: 'google_ai_api_key', label: 'Google AI API Key', category: 'AI', description: 'Gemini models (if not using Emergent LLM Key)', docsUrl: 'https://makersuite.google.com/app/apikey' },
  { id: 'google_oauth_client_id', label: 'Google OAuth Client ID', category: 'Auth', description: 'Google Sign-In', docsUrl: 'https://console.cloud.google.com' },
  { id: 'google_oauth_client_secret', label: 'Google OAuth Secret', category: 'Auth', description: 'Google Sign-In Secret', docsUrl: 'https://console.cloud.google.com' },
  { id: 'stripe_publishable_key', label: 'Stripe Publishable Key', category: 'Payment', description: 'Stripe payments (public key)', docsUrl: 'https://stripe.com/docs' },
  { id: 'stripe_secret_key', label: 'Stripe Secret Key', category: 'Payment', description: 'Stripe payments (secret key)', docsUrl: 'https://stripe.com/docs' },
  { id: 'razorpay_key_id', label: 'Razorpay Key ID', category: 'Payment', description: 'Razorpay payments (India)', docsUrl: 'https://razorpay.com/docs' },
  { id: 'razorpay_key_secret', label: 'Razorpay Secret', category: 'Payment', description: 'Razorpay Secret', docsUrl: 'https://razorpay.com/docs' },
];

export default function CmsIntegrations() {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = ['all', ...new Set(INTEGRATION_CONFIGS.map(c => c.category))];

  const filteredConfigs = INTEGRATION_CONFIGS.filter(config => 
    categoryFilter === 'all' || config.category === categoryFilter
  );

  const handleSave = async (keyId) => {
    try {
      setLoading(true);
      // In a real implementation, save to backend securely
      // await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/integrations/${keyId}`, 
      //   { value: values[keyId] }, 
      //   { withCredentials: true }
      // );
      
      toast.success('Integration key saved (Note: Backend storage not implemented yet)');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save integration key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Plug size={32} weight="fill" className="text-primary" />
          Integrations Center
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage third-party API keys and integrations
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
        <Info size={20} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-600 dark:text-blue-400">
          <p className="font-medium mb-1">Secure Key Storage</p>
          <p className="text-blue-600/80 dark:text-blue-400/80">
            API keys are encrypted and stored securely. Never share your keys publicly.
          </p>
          <p className="text-blue-600/80 dark:text-blue-400/80 mt-2">
            <strong>💡 Recommended:</strong> Use <strong>Emergent LLM Key</strong> (one key for OpenAI, Claude & Gemini) via Profile → Universal Key. 
            Individual API keys below are only needed if you want to use your own provider accounts directly.
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              categoryFilter === cat
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            {cat === 'all' ? 'All' : cat}
          </button>
        ))}
      </div>

      {/* Integration Keys */}
      <div className="space-y-4">
        {filteredConfigs.map(config => (
          <div key={config.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-semibold">{config.label}</h3>
                  <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded">{config.category}</span>
                </div>
                <p className="text-sm text-muted-foreground">{config.description}</p>
              </div>
              <a
                href={config.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Link size={14} />
                Docs
              </a>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <input
                  type={showKeys[config.id] ? 'text' : 'password'}
                  value={values[config.id] || ''}
                  onChange={(e) => setValues({ ...values, [config.id]: e.target.value })}
                  placeholder={`Enter your ${config.label}...`}
                  className="w-full px-4 py-2 pr-24 bg-background border border-border rounded-lg font-mono text-sm"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <button
                    onClick={() => setShowKeys({ ...showKeys, [config.id]: !showKeys[config.id] })}
                    className="p-1 hover:bg-muted rounded"
                  >
                    {showKeys[config.id] ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleSave(config.id)}
                  disabled={!values[config.id] || loading}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Save Key
                </button>
                <button
                  onClick={() => setValues({ ...values, [config.id]: '' })}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredConfigs.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          No integrations in this category
        </div>
      )}
    </div>
  );
}
